import React, { JSX, ReactNode, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import {
  Stack,
  Icon,
  Text,
  DefinitionList,
  HelpLink,
  Tooltip,
  withTranslation,
  Accordion,
  DataTable,
  Card,
  Button,
  formatNumber,
} from '@piwikpro/ui-components';
import { withInject } from '@piwikpro/platform';
import { TrafficMonitor } from '@piwikpro/traffic-monitor-crate';
import MeterChart from '../components/MeterChart';
import { AdministrationState } from '../../../../../redux';

const METER_CHART_MAX_ITEMS = 7;
const BROKEN_EVENT_ID = 17;
const UNLIMITED_TRAFFIC = -1;
const DATA_TABLE_ACCESSORS = {
  actionType: 'actionType',
  currMonth: 'currMonth',
  prevMonth: 'prevMonth',
};
const IGNORE_EVENT_TYPE_IDS = [16, 18, 20, 22];
const ASC = '';
const DSC = '-';

const mapStateToProps = (state: AdministrationState) => ({
  license: state.organization.details.license?.current,
  traffic: state.trafficMonitor.usage,
  capguard: state.organization.details.capguard,
  hasPaddleSubscription: Boolean(state.payments.subscription.details),
  hasScheduledUpdate: state.payments.subscription.details?.scheduled_change_action === 'update',
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

function getMonthlyActionLimitTooltipKey(percentUsed: number): string | null {
  if (percentUsed >= 100) return 'platform.license.traffic.actionsThisMonth.usageInfo100plus';
  if (percentUsed >= 90) return 'platform.license.traffic.actionsThisMonth.usageInfo90to99';
  if (percentUsed >= 80) return 'platform.license.traffic.actionsThisMonth.usageInfo80to89';

  return null;
}

const sortData = (sort, mergedData) => {
  const dataClone = [...mergedData];

  if (sort === '-currMonth') {
    dataClone.sort((a, b) => a.curr.value - b.curr.value);
  } else if (sort === 'currMonth') {
    dataClone.sort((a, b) => b.curr.value - a.curr.value);
  } else if (sort === '-prevMonth') {
    dataClone.sort((a, b) => a.prev.value - b.prev.value);
  } else if (sort === 'prevMonth') {
    dataClone.sort((a, b) => b.prev.value - a.prev.value);
  }

  return dataClone;
};

interface DataTableColumn {
  header: JSX.Element
  accessor?: string
  padding?: 'none' | 'quarter' | 'half' | 'full'
  textAlign?: 'left' | 'center' | 'right'
  sortable?: {
    onClick: () => void
    selected: boolean
    order?: 'asc' | 'dsc'
  }
}

type SortTypes = 'currMonth' | 'prevMonth' | '-currMonth' | '-prevMonth'

interface InjectedProps {
  trafficMonitor: TrafficMonitor
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

export const LicenseDetailsTrafficUsage: React.FC<Props> = ({
  license,
  traffic,
  trafficMonitor,
  hasPaddleSubscription,
  capguard,
  hasScheduledUpdate,
  t,
}) => {
  const eventTypes = trafficMonitor.getTrafficEventTypes().filter(
    eventType => (!IGNORE_EVENT_TYPE_IDS.includes(eventType.id)),
  );
  const [sort, setSort] = useState<SortTypes>('currMonth');
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const prevMonthTraffic = traffic.usage?.[0].traffic;
  const prevMonthTrafficTotal = traffic.usage?.[0].total_traffic;
  const currMonthTraffic = traffic.usage?.[1].traffic;
  const currMonthTrafficTotal = traffic.usage?.[1].total_traffic;
  const trafficLimit = license?.plan.administration?.traffic_limit ?? 0;
  const isTrial = license?.plan.id === 'trial';
  const showPreviousMonthUsage = !isTrial;
  const currMonthTrafficTotalRounded = currMonthTrafficTotal ?? 0;
  const monthlyActionLimitPercentUsed = trafficLimit > 0 ? Math.floor((currMonthTrafficTotalRounded / trafficLimit) * 100) : 0;
  const showMonthlyActionLimitTooltip = !capguard?.is_excluded
    && hasPaddleSubscription
    && !hasScheduledUpdate
    && trafficLimit !== UNLIMITED_TRAFFIC
    && trafficLimit > 0;
  const monthlyActionLimitTooltipKey = showMonthlyActionLimitTooltip
    ? getMonthlyActionLimitTooltipKey(monthlyActionLimitPercentUsed)
    : null;

  let tableData: {
    string: {
      content: ReactNode
      padding: 'none' | 'quarter' | 'half' | 'full'
    }
  }[] = [];

  const prepareChartData = (allTraffic, trafficTotal) => {
    if (!trafficTotal) {
      return [];
    }

    const chartData = Object.keys(allTraffic).map(key => [Number(key), allTraffic[key]])
      .filter(([id]) => id !== BROKEN_EVENT_ID)
      .sort(([, a], [, b]) => a - b)
      .reverse()
      .slice(0, METER_CHART_MAX_ITEMS);
    const chartDataTotal = chartData.reduce((r, [, v]) => r + v, 0);
    const otherEvents = trafficTotal - chartDataTotal;

    const returnValue = chartData
      .reduce((r, [k, v]) => ([
        ...r,
        {
          id: k,
          name: t(eventTypes.find(e => e.id === k)?.key || ''),
          value: v,
        },
      ]), [])
      .filter(entry => entry.name); // remove unmatched events (line 158)

    returnValue.push({
      id: 99,
      name: t('platform.license.traffic.meter-chart.event-type.other.title'),
      value: otherEvents,
    });

    return returnValue;
  };

  const prepareDataTableData = (sortedData, prevTotal, currTotal) => {
    const totalSummaryRow = [{
      [DATA_TABLE_ACCESSORS.actionType]: {
        content: null,
        padding: 'full',
      },
      [DATA_TABLE_ACCESSORS.currMonth]: {
        content: (
          <Text as="p" weight={600}>
            {formatNumber(
              currTotal ?? 0,
            )}
          </Text>
        ),
        padding: 'full',
        textAlign: 'right',
      },
      [DATA_TABLE_ACCESSORS.prevMonth]: {
        content: (
          <Text as="p" weight={600}>
            {formatNumber(
              prevTotal ?? 0,
            )}

          </Text>
        ),
        padding: 'full',
        textAlign: 'right',
      },
    }];

    const eventsData = sortedData.map(event => ({
      [DATA_TABLE_ACCESSORS.actionType]: {
        content: (
          event.key === 'custom-reports:debugger.event-type.broken-event' ? (
            <Stack horizontalAlignment="left" verticalAlignment="center" spacing="extraNarrow">
              <Stack.Item>
                <Text as="p" color="Red">{t(event.key)}</Text>
              </Stack.Item>
              <Stack.Item>
                <Tooltip content={t('platform.license.traffic.dataTable.event-type.broken-event.info')}>
                  <div>
                    <Icon name="info-circle-outline" color="red" />
                  </div>
                </Tooltip>
              </Stack.Item>
            </Stack>
          ) : (
            <Text dataId="CellTextStyle">{t(event.key)}</Text>
          )
        ),
        padding: 'full',
      },
      [DATA_TABLE_ACCESSORS.currMonth]: {
        content: (
          <div>
            <Text
              color={event.key === 'custom-reports:debugger.event-type.broken-event' ? 'Red' : undefined}
              wrapType="noWrap"
            >
              {formatNumber(event.curr.value)}
            </Text>
            <br />
            <Text size={12} color="InkLighter" wrapType="noWrap" dataId="Caption">
              {`${event.curr.percentage}%`}
            </Text>
          </div>
        ),
        padding: 'full',
        textAlign: 'right',
      },
      [DATA_TABLE_ACCESSORS.prevMonth]: {
        content: (
          <div>
            <Text
              color={event.key === 'custom-reports:debugger.event-type.broken-event' ? 'Red' : 'Ink'}
              wrapType="noWrap"
            >
              {formatNumber(event.prev.value)}
            </Text>
            <br />
            <Text size={12} color="InkLighter" wrapType="noWrap" dataId="Caption">
              {`${event.prev.percentage}%`}
            </Text>
          </div>
        ),
        padding: 'full',
        textAlign: 'right',
      },
    }));

    return [...totalSummaryRow, ...eventsData];
  };

  const handleSortIcons = (columnName) => {
    if (sort.replace(DSC, ASC) === columnName) {
      return sort.includes(DSC) ? 'asc' : 'dsc';
    }

    return undefined;
  };

  const handleSortChange = (columnName) => {
    const sortWithDirection = sort === columnName ? `-${columnName}` : columnName;

    setSort(sortWithDirection);
  };

  const handleDownloadReport = async () => {
    if (isDownloadingReport) {
      return;
    }

    setIsDownloadingReport(true);

    try {
      await trafficMonitor.downloadUsagePerAppReport();
    } catch {
      // eslint-disable-line no-empty
    }

    setIsDownloadingReport(false);
  };

  const prepareMergedData = (prev, curr, prevTotal, currTotal) => {
    const percentageOf = (value, total) => {
      const percentage = Math.round((value * 100) / total);

      // eslint-disable-next-line no-restricted-globals
      if (isNaN(percentage)) return '0';
      if (percentage === 0) return '<1';
      return String(percentage);
    };

    return eventTypes.map(event => ({
      id: event.id,
      key: event.key,
      prev: {
        value: prev[event.id] || 0,
        percentage: percentageOf(prev[event.id], prevTotal),
      },
      curr: {
        value: curr[event.id] || 0,
        percentage: percentageOf(curr[event.id], currTotal),
      },
    }));
  };

  const dataTableColumns = (
    previousMonthTrafficTotal?: number, currentMonthTrafficTotal?: number,
  ) => {
    const eventTypeColumn: DataTableColumn = {
      header: (
        <div style={{ paddingTop: '8px', paddingBottom: '8px' }}>
          <Text as="p" weight={600} wrapType="wrap" dataId="Label">
            {t('platform.license.traffic.dataTable.columnName.actionType')}
          </Text>
        </div>
      ),
      accessor: DATA_TABLE_ACCESSORS.actionType,
      padding: 'full',
      textAlign: 'left',
    };
    const currMonthColumn: DataTableColumn = {
      header: t('platform.license.traffic.dataTable.columnAction.thisMonth'),
      sortable: {
        onClick: () => handleSortChange('currMonth'),
        selected: sort.includes('currMonth'),
        order: handleSortIcons('currMonth'),
      },
      textAlign: 'center',
      accessor: DATA_TABLE_ACCESSORS.currMonth,
      padding: 'full',
    };
    const prevMonthColumn: DataTableColumn = {
      header: t('platform.license.traffic.dataTable.columnAction.lastMonth'),
      sortable: {
        onClick: () => handleSortChange('prevMonth'),
        selected: sort.includes('prevMonth'),
        order: handleSortIcons('prevMonth'),
      },
      accessor: DATA_TABLE_ACCESSORS.prevMonth,
      padding: 'full',
      textAlign: 'center',
    };
    if (currentMonthTrafficTotal && previousMonthTrafficTotal) {
      return [
        eventTypeColumn,
        currMonthColumn,
        prevMonthColumn,
      ];
    }
    if (previousMonthTrafficTotal) {
      return [
        eventTypeColumn,
        prevMonthColumn,
      ];
    }

    if (currentMonthTrafficTotal) {
      return [
        eventTypeColumn,
        currMonthColumn,
      ];
    }

    return [];
  };
  const chartData = prepareChartData(currMonthTraffic, currMonthTrafficTotal);

  let actionsThisMonthDefinitionValue: string | JSX.Element;

  if (trafficLimit === UNLIMITED_TRAFFIC) {
    actionsThisMonthDefinitionValue = t('platform.license.traffic.usage.value', {
      used: formatNumber(
        currMonthTrafficTotal ?? 0,
      ),
    });
  } else {
    const progressTextProps = {
      used: formatNumber(
        currMonthTrafficTotal ?? 0,
      ),
      total: formatNumber(
        trafficLimit,
      ),
    };
    const monthlyUsageShowsRed = currMonthTrafficTotal! >= trafficLimit
      || monthlyActionLimitPercentUsed >= 100;

    const progressTranslated = t(
      'platform.license.type.traffic-limit.progressNew.info',
      progressTextProps,
    );

    let progressTextNode: JSX.Element;

    if (monthlyUsageShowsRed) {
      progressTextNode = (
        <Text as="p" color="Red" wrapType="wrap">
          {progressTranslated}
        </Text>
      );
    } else if (monthlyActionLimitPercentUsed >= 80) {
      progressTextNode = (
        <Text as="p" wrapType="wrap">
          <span style={{ color: 'var(--vOrangeDark)' }}>
            {progressTranslated}
          </span>
        </Text>
      );
    } else {
      progressTextNode = (
        <Text as="p" wrapType="wrap">
          {progressTranslated}
        </Text>
      );
    }

    if (!monthlyActionLimitTooltipKey) {
      actionsThisMonthDefinitionValue = progressTextNode;
    } else {
      actionsThisMonthDefinitionValue = (
        <Stack horizontalAlignment="right" verticalAlignment="center" spacing="narrow">
          <Stack.Item>
            <Tooltip content={t(monthlyActionLimitTooltipKey, { percent: monthlyActionLimitPercentUsed })}>
              <Icon
                name="info-circle"
                color={monthlyUsageShowsRed ? 'red' : 'dark-orange'}
              />
            </Tooltip>
          </Stack.Item>
          <Stack.Item>
            {progressTextNode}
          </Stack.Item>
        </Stack>
      );
    }
  }

  if (prevMonthTraffic && currMonthTraffic) {
    const mergedData = prepareMergedData(
      prevMonthTraffic,
      currMonthTraffic,
      prevMonthTrafficTotal,
      currMonthTrafficTotal,
    );
    const sortedTableData = sortData(sort, mergedData);
    tableData = prepareDataTableData(
      sortedTableData,
      prevMonthTrafficTotal,
      currMonthTrafficTotal,
    );
  }

  return (
    <>
      <Stack vertical horizontalAlignment="middle">
        <Stack.Item>
          <DefinitionList
            items={[
              {
                title: t('platform.license.actions.usedInThisMonth'),
                value: actionsThisMonthDefinitionValue,
              },
            ]}
            textAlign={['left', 'right']}
          />
          <div style={{ marginTop: '-4px' }}>
            {currMonthTrafficTotal ? (
              <MeterChart
                data={chartData}
                withLegend
              />
            ) : <div style={{ marginTop: '-16px' }} />}
          </div>

        </Stack.Item>
        {
          showPreviousMonthUsage && (
            <Stack.Item>
              <div style={{ marginTop: '-8px' }}>
                <DefinitionList
                  items={[
                    {
                      title: t('platform.license.actions.usedInLastMonth'),
                      value: t('platform.license.traffic.usage.value', {
                        used: formatNumber(
                          prevMonthTrafficTotal ?? 0,
                        ),
                      }),
                    },
                  ]}
                  textAlign={['left', 'right']}
                />
              </div>
            </Stack.Item>
          )
        }
        {(currMonthTrafficTotal || prevMonthTrafficTotal) ? (
          <>
            <Stack.Item fill previousSpacing={showPreviousMonthUsage ? 'narrow' : undefined}>
              <div style={{ margin: `${showPreviousMonthUsage ? '-16px' : '0px'} 0px -16px -16px` }}>
                <Accordion
                  text={<Text as="p" weight={600} dataId="Label" wrapType="wrap">{t('platform.license.traffic.dataTable.accordion.text')}</Text>}
                  hideSeparator
                >
                  <div style={{ marginRight: '-16px' }}>
                    <DataTable
                      fullWidth
                      fullHeight
                      extraHeaders={1}
                      columns={dataTableColumns(
                        prevMonthTrafficTotal,
                        currMonthTrafficTotal,
                      ) as (
                        DataTableColumn & { columns: DataTableColumn }
                      )[]}
                      data={tableData}
                    />
                  </div>
                </Accordion>
              </div>
            </Stack.Item>
            <Stack.Item fill>
              <Card>
                <Card.Section padding="narrow">
                  <Stack verticalAlignment="center">
                    <Stack.Item fill>
                      <Stack
                        vertical
                        spacing="extraNarrow"
                        verticalAlignment="center"
                      >
                        <Stack.Item>
                          <Text size={12} as="p" weight={600}>
                            {t('platform.license.traffic.usage.report.title')}
                          </Text>
                        </Stack.Item>
                        <Stack.Item>
                          <Text as="p" size={12} color="InkLighter">
                            {t('platform.license.traffic.usage.report.description')}
                          </Text>
                        </Stack.Item>
                      </Stack>
                    </Stack.Item>
                    <Stack.Item>
                      <Button
                        icon="download"
                        dataId="DownloadTrafficPerAppReportButton" // tag attached for analytics
                        text={t('platform.license.traffic.usage.report.button')}
                        appearance="simple"
                        disabled={isDownloadingReport}
                        onClick={handleDownloadReport}
                      />
                    </Stack.Item>
                  </Stack>
                </Card.Section>
              </Card>
            </Stack.Item>
          </>
        ) : null}
        <Stack.Item>
          <HelpLink href="https://help.piwik.pro/support/questions/how-can-i-estimate-the-number-of-actions-for-the-pricing/">
            How can I estimate the number of actions for the pricing?
          </HelpLink>
        </Stack.Item>
      </Stack>
    </>
  );
};

export default connector(withInject<InjectedProps>({
  trafficMonitor: 'TrafficMonitorCrate.trafficMonitor',
})(withTranslation(['administration', 'custom-reports'])(LicenseDetailsTrafficUsage as any)));
